import { FC, ReactNode, useEffect, useState } from 'react'

import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { createSearchParams, useNavigate } from 'react-router-dom'

import { RoutingPaths } from 'app/routes/routes'

import { PassengersValues } from 'pages/avia/purchase-form/model'

import { CloseCircle } from 'entities/close-circle/model'
import { AddCloseCircleForm } from 'entities/close-circle/ui/add-close-circle-form'

import { CloseCircleApi } from 'shared/api/close-circle'
import {
  AVAILABLE_ID_PASSPORT,
  MAX_LENGTH_CLOSE_CIRCLE,
  TYPE_AGE,
} from 'shared/constants'
import ErrorImage from 'shared/images/error.png'
import SuccessImage from 'shared/images/tick_bold.png'
import { getErrorMessage } from 'shared/lib/errorText'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { Loader } from 'shared/ui/loader'
import { Paper } from 'shared/ui/paper'
import { PopupContent } from 'shared/ui/poup-content'
import { BlockSelect } from 'shared/ui/select/components/select-block'

import { FormPassengersValues } from '../../models/form-passengers'

import { Header } from './ui/header'

import {
  checkAddCloseCircle,
  getPreparedCreateCloseCircle,
  preparedCloseCircleForm,
} from './lib'
import s from './styles.module.scss'

interface Props {
  children: ReactNode
  keyPassenger: {
    age: TYPE_AGE
    index: number
  }
  info: {
    count: number
    subText: string
  }
  isAvailableCountries?: boolean
}

export const PurchaseCloseCircle: FC<Props> = ({
  children,
  keyPassenger,
  info,
  isAvailableCountries,
}) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const { closeDialog, openDialog } = useDialogBottomSheet()

  const { getValues, control } = useFormContext<FormPassengersValues>()

  const [closeCircles, setCloseCircle] = useState<CloseCircle[]>()
  const [loading, setLoading] = useState<boolean>()

  const { age, index } = keyPassenger
  const { count, subText } = info

  const formName = `${age}.${index}` as const

  const { update } = useFieldArray({
    control,
    name: age,
  })

  const getCircle = async () => {
    try {
      const { data: CloseCircle } = await CloseCircleApi.getUserDocuments()

      setCloseCircle(CloseCircle.data)
    } catch (e: unknown) {}
  }

  const onShowCloseCircle = () => {
    const value = getValues(formName) as PassengersValues

    const listCircle = closeCircles?.map((circle) => ({
      value: `${circle.id}`,
      name: `${circle?.label || ''}`,
    }))

    openDialog({
      child: (
        <div className={s.dialog}>
          <BlockSelect
            listOption={listCircle}
            value={{ value: `${value.id}`, name: '' }}
            onChange={(selected) => {
              const selectedCircle = closeCircles?.filter(
                (circle) => circle.id === selected?.value,
              )[0]

              const isAvailableCitizenship = AVAILABLE_ID_PASSPORT.includes(
                String(selectedCircle?.citizenship).toUpperCase(),
              )

              if (!selectedCircle?.first_name) return

              const dataPrepared = preparedCloseCircleForm(
                age,
                selectedCircle,
                isAvailableCitizenship && isAvailableCountries,
              )

              update(index, dataPrepared)

              closeDialog()
            }}
          />
        </div>
      ),
    })
  }

  const onAddCloseCircle = async () => {
    const currentPassenger = getValues(formName) as PassengersValues

    try {
      await checkAddCloseCircle.validate(currentPassenger)

      const preparedPassenger = getPreparedCreateCloseCircle(currentPassenger)

      openDialog({
        isDialog: false,
        child: (
          <AddCloseCircleForm
            onCreate={(label) =>
              onSetCloseCircleRequest({ ...preparedPassenger, label })
            }
            onClose={closeDialog}
          />
        ),
      })
    } catch {
      openDialog({
        isDialog: false,
        child: (
          <PopupContent
            image={ErrorImage}
            button={t('close')}
            title={t('error')}
            description={t('close_circle.please')}
            onClick={closeDialog}
          />
        ),
      })
    }
  }

  const onSetCloseCircleRequest = async (passengerValue: CloseCircle) => {
    setLoading(true)
    closeDialog()

    try {
      await CloseCircleApi.setUserDocuments(passengerValue)

      await getCircle()

      openDialog({
        isDialog: false,
        child: (
          <PopupContent
            image={SuccessImage}
            button={t('continue')}
            title={t('сlose_circlу')}
            description={t('close_circle.add_pop_desc')}
            onClick={closeDialog}
          />
        ),
      })
    } catch (error) {
      const errorText = getErrorMessage(error)

      openDialog({
        isDialog: false,
        child: (
          <PopupContent
            image={ErrorImage}
            button={t('close')}
            title={t('error')}
            onClick={closeDialog}
            description={errorText}
          />
        ),
      })
    } finally {
      setLoading(false)
    }
  }

  const onScanPassport = () => {
    navigate({
      pathname: RoutingPaths.avia.scanPassword.path(),
      search: createSearchParams({
        age: `${age}`,
        passenger: `${index}`,
      }).toString(),
    })
  }

  useEffect(() => {
    getCircle()
  }, [])

  return (
    <div className={s.container}>
      {loading && (
        <div className={s.wrapperLoader}>
          <Loader marginTop={150} />
        </div>
      )}
      <Paper padding={[20, 16]}>
        <div className={s.header}>
          <div className={s.left}>
            <p className={s.title}>{`${t('passenger_each')} ${count}`}</p>
            <p className={s.subTitle}>{subText}</p>
          </div>

          {/* {Boolean(closeCircles?.length) && (
            <div
              className={s.right}
              onClick={onShowCloseCircle}
            >
              <AppIcon
                id={AllIcons.PERSONS}
                className={s.icon}
                height={20}
                width={20}
              />

              <span>{t('сlose_circlу')}</span>
            </div>
          )} */}

          <Header
            showButtonCloseCircle={closeCircles?.length}
            onShowCloseCircle={onShowCloseCircle}
            onScanPassport={onScanPassport}
          />
        </div>

        {children}

        {(closeCircles?.length || 0) < MAX_LENGTH_CLOSE_CIRCLE && (
          <div
            className={s.addCloseCircle}
            onClick={() => onAddCloseCircle()}
          >
            <AppIcon
              id={AllIcons.PLUS}
              height={12}
              width={12}
              className={s.add}
            />

            <span>{t('close_circle.add')}</span>
          </div>
        )}
      </Paper>
    </div>
  )
}
